import { useEffect, useState } from 'react';

import { ApiError, api } from '../api/client';
import { formatDateTime } from '../lib/dateFormat';
import { useLanguage } from '../lib/i18n/LanguageContext';

type QuotaStatus = {
  tokens_used: number;
  tokens_cap: number;
  requests_used: number;
  requests_cap: number;
  resets_at: string;
};

type Props = {
  /** 'compact' = Overview (percentage only); 'full' = Usage page (used / cap numbers) */
  variant: 'compact' | 'full';
};

function pct(used: number, cap: number): number {
  if (cap <= 0) return 0;
  return Math.min(100, Math.round((used / cap) * 100));
}

function QuotaRow({
  label,
  used,
  cap,
  variant,
}: {
  label: string;
  used: number;
  cap: number;
  variant: 'compact' | 'full';
}) {
  const p = pct(used, cap);
  const exceeded = used >= cap;
  return (
    <div className="fq-row">
      <div className="fq-row-meta">
        <span className="fq-label">{label}</span>
        {variant === 'full' ? (
          <span className={`fq-numbers${exceeded ? ' fq-numbers--exceeded' : ''}`}>
            {used.toLocaleString()} / {cap.toLocaleString()}
          </span>
        ) : (
          <span className={`fq-pct${exceeded ? ' fq-pct--exceeded' : ''}`}>{p}%</span>
        )}
      </div>
      <div className="fq-track" role="progressbar" aria-valuenow={p} aria-valuemin={0} aria-valuemax={100}>
        <div
          className={`fq-fill${exceeded ? ' fq-fill--exceeded' : ''}`}
          style={{ width: `${p}%` }}
        />
      </div>
    </div>
  );
}

export function FreeQuotaCard({ variant }: Props) {
  const { dict } = useLanguage();
  const u = dict.usage;
  const [quota, setQuota] = useState<QuotaStatus | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .quotaStatus()
      .then((q) => { if (!cancelled) setQuota(q); })
      .catch((e) => {
        // 403 = no org yet (onboarding) — silently hide the card.
        if (!cancelled && !(e instanceof ApiError && e.status === 403)) setErr(true);
      });
    return () => { cancelled = true; };
  }, []);

  if (err || quota === null) return null;

  const tokExceeded = quota.tokens_used >= quota.tokens_cap;
  const reqExceeded = quota.requests_used >= quota.requests_cap;
  const anyExceeded = tokExceeded || reqExceeded;

  return (
    <div className={`panel fq-card fq-card--${variant}${anyExceeded ? ' fq-card--exceeded' : ''}`}>
      <div className="fq-head">
        <div className="fq-head-left">
          <span className="fq-title">{u.quotaCardTitle}</span>
          {anyExceeded && (
            <span className="badge badge--failed fq-exceeded-badge">{u.quotaExceeded}</span>
          )}
        </div>
        <span className="fq-resets note">{u.quotaResetsAt(formatDateTime(quota.resets_at))}</span>
      </div>

      <div className="fq-rows">
        <QuotaRow label={u.quotaTokensLabel}   used={quota.tokens_used}   cap={quota.tokens_cap}   variant={variant} />
        <QuotaRow label={u.quotaRequestsLabel} used={quota.requests_used} cap={quota.requests_cap} variant={variant} />
      </div>

    </div>
  );
}
